import { SprintEntity } from '@data';
import { useQueries, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';

import * as api from '../api';
import * as queryKeys from '../constants/keys';

import type {
  IError,
  ListResponse,
  ReportEntity,
  SprintProductEntity,
  SprintProductReportsParams,
} from '../utils/types';

export const useSprint = (sprintId?: string): UseQueryResult<SprintEntity, IError> => {
  const queryClient = useQueryClient();
  const enabled = !!sprintId;
  return useQuery({
    queryKey: [queryKeys.SPRINT, sprintId],
    queryFn: () => api.fetchSprint(sprintId!),
    enabled,
    staleTime: 1000,
    placeholderData: () => {
      const lists = queryClient.getQueriesData<ListResponse<SprintEntity>>({
        queryKey: [queryKeys.SPRINTS],
        exact: false,
      });
      return lists.flatMap(([, data]) => data?.items ?? []).find(sprint => sprint.id === sprintId);
    },
  });
};

export const useListSprints = (params?: {
  from?: string | Date;
  to?: string | Date;
  offset?: number;
  limit?: number;
}): UseQueryResult<{ items: SprintEntity[]; count: number }, IError> =>
  useQuery({
    queryKey: [queryKeys.SPRINTS, params],
    queryFn: () => api.fetchSprints(params),
  });

export const useSprintProductReports = (
  params: SprintProductReportsParams,
): UseQueryResult<ListResponse<ReportEntity>, IError> =>
  useQuery({
    queryKey: [queryKeys.PM_SPRINT_PRODUCT_REPORTS, params],
    queryFn: () => api.fetchSprintProductReports(params),
    enabled: !!params.sprint,
  });

/** Keyed like the single-sprint hook, so a sprint fetched either way is one cache entry. */
export const useSprintProductReportsBySprint = (
  sprints: SprintEntity[],
  productId: string,
  { incidentWork, enabled = true }: { incidentWork?: boolean; enabled?: boolean } = {},
): { bySprint: Record<string, ReportEntity[]>; isLoading: boolean; isError: boolean } => {
  const results = useQueries({
    queries: sprints.map(sprint => ({
      queryKey: [queryKeys.PM_SPRINT_PRODUCT_REPORTS, { sprint, productId, incidentWork }],
      queryFn: () => api.fetchSprintProductReports({ sprint, productId, incidentWork }),
      enabled: !!productId && enabled,
    })),
  });

  return {
    bySprint: Object.fromEntries(sprints.map((sprint, index) => [sprint.id, results[index]?.data?.items ?? []])),
    isLoading: results.some(result => result.isLoading),
    // One failed sprint is a year the cards cannot report: their buckets fall back to empty.
    isError: results.some(result => result.isError),
  };
};

export const useListSprintProducts = (params: {
  sprintId: string | undefined;
  productId: string | undefined;
  asClient?: boolean;
}): UseQueryResult<ListResponse<SprintProductEntity>, IError> =>
  useQuery({
    queryKey: [queryKeys.PM_SPRINT_PRODUCTS, params],
    queryFn: () => api.fetchSprintProducts(params),
    enabled: !!params.productId && !!params.sprintId,
  });
